import { a as _objectWithoutProperties, j as _slicedToArray, k as _objectSpread2, e as _defineProperty, b as _extends } from './_rollupPluginBabelHelpers-6a814b33.js';
import React, { useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { g as globalPropTypes } from './globalPropTypes-bb98efc9.js';
import { g as globalClasses } from './globalClasses-ae52730b.js';
import { makeQAAttributes } from './utils/qaAttributes.js';
import { s as styles } from './InputControl-bf88d46d.js';

var Input = React.forwardRef(function (_ref, inputRef) {
  var _cx, _cx2;

  var cmpAfter = _ref.cmpAfter,
      cmpBefore = _ref.cmpBefore,
      cmpError = _ref.cmpError,
      autocomplete = _ref.autocomplete,
      autoFocus = _ref.autoFocus,
      css = _ref.css,
      disabled = _ref.disabled,
      id = _ref.id,
      name = _ref.name,
      maxLength = _ref.maxLength,
      placeholder = _ref.placeholder,
      type = _ref.type,
      value = _ref.value,
      readonly = _ref.readonly,
      inaccessible = _ref.inaccessible,
      invalid = _ref.invalid,
      required = _ref.required,
      onBlur = _ref.onBlur,
      onFocus = _ref.onFocus,
      onChange = _ref.onChange,
      onInput = _ref.onInput,
      onKeyDown = _ref.onKeyDown,
      onKeyUp = _ref.onKeyUp,
      props = _objectWithoutProperties(_ref, ["cmpAfter", "cmpBefore", "cmpError", "autocomplete", "autoFocus", "css", "disabled", "id", "name", "maxLength", "placeholder", "type", "value", "readonly", "inaccessible", "invalid", "required", "onBlur", "onFocus", "onChange", "onInput", "onKeyDown", "onKeyUp"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isFocused = _useState2[0],
      setFocused = _useState2[1];

  var mergedCSS = _objectSpread2({}, Input.defaultProps.css, {}, css);

  var theme = mergedCSS.theme,
      width = mergedCSS.width,
      height = mergedCSS.height,
      padding = mergedCSS.padding,
      paddingTop = mergedCSS.paddingTop,
      paddingRight = mergedCSS.paddingRight,
      paddingBottom = mergedCSS.paddingBottom,
      paddingLeft = mergedCSS.paddingLeft,
      behavior = mergedCSS.behavior,
      fontFamily = mergedCSS.fontFamily;
  var componentClassName = cx(styles['input-control'], (_cx = {}, _defineProperty(_cx, styles["behavior-".concat(behavior)], behavior), _defineProperty(_cx, styles["height-".concat(height)], height), _defineProperty(_cx, styles["width-".concat(width)], width), _defineProperty(_cx, styles["theme-".concat(theme)], theme), _defineProperty(_cx, styles['is-focused'], isFocused), _defineProperty(_cx, styles['is-disabled'], disabled), _defineProperty(_cx, styles['is-invalid'], invalid), _defineProperty(_cx, styles['is-required'], required), _defineProperty(_cx, styles['is-inaccessible'], inaccessible), _cx));
  var elementClassName = cx(styles['input-control__element'], (_cx2 = {}, _defineProperty(_cx2, styles["width-".concat(width)], width), _defineProperty(_cx2, styles['is-focused'], isFocused), _cx2), globalClasses({
    padding: padding,
    paddingTop: paddingTop,
    paddingRight: paddingRight,
    paddingBottom: paddingBottom,
    paddingLeft: paddingLeft,
    fontFamily: fontFamily
  }));

  var handleKeyUp = function handleKeyUp(e) {
    if (e.key === 'Tab') {
      setFocused(true);
    }

    if (typeof onKeyUp === 'function') {
      onKeyUp(e);
    }
  };

  var inputFocusHandler = function inputFocusHandler() {
    setFocused(true);
    onFocus();
  };

  var inputBlurHandler = function inputBlurHandler() {
    setFocused(false);
    onBlur();
  };

  return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: componentClassName
  }, cmpBefore && /*#__PURE__*/React.createElement("div", {
    className: styles['input-control__before']
  }, cmpBefore), /*#__PURE__*/React.createElement("div", {
    className: styles['input-control__main']
  }, /*#__PURE__*/React.createElement("input", _extends({
    id: id,
    ref: inputRef,
    className: elementClassName,
    type: type,
    name: name,
    autoFocus: autoFocus // eslint-disable-line jsx-a11y/no-autofocus
    ,
    maxLength: maxLength,
    placeholder: placeholder,
    value: value,
    readOnly: readonly,
    disabled: disabled,
    "aria-invalid": String(invalid),
    "aria-required": String(required),
    onBlur: inputBlurHandler,
    onFocus: inputFocusHandler,
    onKeyUp: handleKeyUp,
    onKeyDown: onKeyDown,
    onChange: onChange,
    onInput: onInput,
    autoComplete: autocomplete
  }, props))), cmpAfter && /*#__PURE__*/React.createElement("div", {
    className: styles['input-control__after']
  }, cmpAfter)), cmpError);
});
Input.displayName = 'Input';
var InputPropTypes = {
  id: PropTypes.string,
  autocomplete: PropTypes.oneOf(['on', 'off']),
  autoFocus: PropTypes.bool,
  disabled: PropTypes.bool,
  invalid: PropTypes.bool,
  maxLength: PropTypes.number,
  required: PropTypes.bool,
  readonly: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  inaccessible: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onChange: PropTypes.func,
  onInput: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func
};
var InputDefaultProps = {
  id: null,
  autocomplete: 'off',
  autoFocus: false,
  disabled: false,
  invalid: false,
  required: false,
  maxLength: null,
  value: '',
  placeholder: null,
  name: null,
  readonly: false,
  inaccessible: false,
  onBlur: function onBlur() {},
  onFocus: function onFocus() {},
  onChange: function onChange() {},
  onInput: function onInput() {},
  onKeyDown: function onKeyDown() {},
  onKeyUp: function onKeyUp() {}
};
Input.propTypes = _objectSpread2({}, InputPropTypes, {
  css: PropTypes.shape({
    theme: PropTypes.oneOf(['flat-gray', 'underline-gray', 'transparent-orange']),
    behavior: PropTypes.oneOf(['show-indicator-on-hover']),
    width: PropTypes.oneOf(['100pct']),
    height: PropTypes.oneOf(['100pct', 30, 40]),
    padding: globalPropTypes('padding'),
    paddingTop: globalPropTypes('paddingLeft'),
    paddingRight: globalPropTypes('paddingRight'),
    paddingBottom: globalPropTypes('paddingBottom'),
    paddingLeft: globalPropTypes('paddingLeft')
  }),
  cmpAfter: PropTypes.element,
  cmpBefore: PropTypes.element,
  type: PropTypes.oneOf(['text', 'number', 'password', 'url', 'email']),
  cmpError: PropTypes.element
});
Input.defaultProps = _objectSpread2({}, InputDefaultProps, {
  css: {
    theme: 'flat-gray',
    fontFamily: 'open-sans',
    behavior: null,
    width: '100pct',
    height: 30,
    padding: null,
    paddingTop: 6,
    paddingRight: 8,
    paddingBottom: 6,
    paddingLeft: 8
  },
  cmpBefore: null,
  cmpAfter: null,
  type: 'text',
  cmpError: null
}, makeQAAttributes('input'));

export { Input as I, InputPropTypes as a, InputDefaultProps as b };
//# sourceMappingURL=Input-511382f8.js.map
